import {
  Injectable,
  Signal,
  WritableSignal,
  computed,
  effect,
  signal,
  inject,
} from "@angular/core";
import { Observable, tap, of } from "rxjs";

import { DocumentsApiService } from "../../api/documents/documents-api.service";
import {
  CreateDocumentDto,
  UpdateDocumentDto,
  FilterDocumentDto,
  DocumentStatsDto,
  ResponseDocumentDto,
  DocumentStatsResponseDto,
  BulkOperationResultDto,
  DocumentOperationResponseDto,
} from "../../../api/models";
import { CachedServiceBase } from "../../base/cached-service.base";

/**
 * Cached Documents Service that extends base functionality and uses API service
 */
@Injectable({ providedIn: "root" })
export class CachedDocumentsService extends CachedServiceBase<ResponseDocumentDto> {
  private readonly _documentsApiService = inject(DocumentsApiService);

  // ========== BASIC FILTERS FOR INITIAL LOAD ==========
  private readonly _patientId: WritableSignal<string | null> = signal(null);
  private readonly _documentType: WritableSignal<string | null> = signal(null);

  // ========== CLIENT-SIDE FILTERS (real-time) ==========
  public readonly fileNameFilter: WritableSignal<string> = signal("");
  public readonly serviceTitleFilter: WritableSignal<string> = signal("");
  public readonly serviceCodeFilter: WritableSignal<string> = signal("");
  public readonly addedDateFromFilter: WritableSignal<Date | undefined> =
    signal(undefined);
  public readonly addedDateToFilter: WritableSignal<Date | undefined> = signal(undefined);

  // ========== ADDITIONAL STATE ==========
  // Note: Pagination is now handled client-side since backend returns array

  constructor() {
    super();

    // Effect to reset pagination when client-side filters change
    effect(() => {
      this.fileNameFilter();
      this.serviceTitleFilter();
      this.serviceCodeFilter();
      this.addedDateFromFilter();
      this.addedDateToFilter();

      // Reset to first page when filters change
      this.resetPagination();
    });
  }

  // ========== IMPLEMENT ABSTRACT METHODS ==========

  /**
   * Apply client-side filters to backend data
   */
  protected getFilteredData(): Signal<ResponseDocumentDto[]> {
    return computed(() => {
      const data = this.data();
      const fileNameFilter = this.fileNameFilter().toLowerCase();
      const serviceTitleFilter = this.serviceTitleFilter().toLowerCase();
      const serviceCodeFilter = this.serviceCodeFilter().toLowerCase();
      const addedDateFrom = this.addedDateFromFilter();
      const addedDateTo = this.addedDateToFilter();

      const filtered = data.filter((doc) => {
        // File name filter
        if (
          fileNameFilter &&
          !doc.title?.toLowerCase().includes(fileNameFilter)
        ) {
          return false;
        }

        // Service title filter (for referti)
        if (
          (serviceTitleFilter &&
          doc.testTitle &&
          !doc.testTitle.toLowerCase().includes(serviceTitleFilter)) ||
          (serviceTitleFilter &&
          doc.visitTitle &&
          !doc.visitTitle.toLowerCase().includes(serviceTitleFilter))
        ) {
          return false;
        }

        // Service code filter (for referti)
        if (
          (serviceCodeFilter &&
          doc.testCode &&
          !doc.testCode.toLowerCase().includes(serviceCodeFilter)) ||
          (serviceCodeFilter &&
          doc.visitCode &&
          !doc.visitCode.toLowerCase().includes(serviceCodeFilter))
        ) {
          return false;
        }

        // Date added filters
        if (addedDateFrom || addedDateTo) {
          const docDate = new Date(doc.createdAt);
          if (addedDateFrom && docDate < addedDateFrom) {
            return false;
          }
          if (addedDateTo && docDate > addedDateTo) {
            return false;
          }
        }

        return true;
      });

      return filtered;
    });
  }

  public refresh(): void {
    const currentFilters = {
      patientId: this._patientId() || undefined,
      documentType: this._documentType() || undefined,
    };
    this._loadData(currentFilters as Partial<FilterDocumentDto>);
  }

  public reset(): void {
    this.clearData();
    this.clearAllFilters();
  }

  // ========== INITIAL LOAD METHODS ==========

  public setPatientId(patientId: string | null): void {
    this._patientId.set(patientId);
  }

  public setDocumentType(documentType: string | null): void {
    this._documentType.set(documentType);
  }

  /**
   * Load data with current basic filters (used for initial load)
   */
  public loadData(): void {
    const filters = {
      patientId: this._patientId() || undefined,
      documentType: this._documentType() || undefined,
    };
    this._loadData(filters as Partial<FilterDocumentDto>);
  }

  /**
   * Check if there are active filters for showing reset button
   */
  public hasActiveFilters(): boolean {
    return !!(
      this.fileNameFilter().trim() ||
      this.serviceTitleFilter().trim() ||
      this.serviceCodeFilter().trim() ||
      this.addedDateFromFilter() ||
      this.addedDateToFilter()
    );
  }

  // ========== CLIENT-SIDE FILTER MANAGEMENT ==========

  public clearClientFilters(): void {
    this.fileNameFilter.set("");
    this.serviceTitleFilter.set("");
    this.serviceCodeFilter.set("");
    this.addedDateFromFilter.set(undefined);
    this.addedDateToFilter.set(undefined);
  }

  public clearAllFilters(): void {
    // Clear initial load filters
    this._patientId.set(null);
    this._documentType.set(null);

    // Clear client-side filters
    this.clearClientFilters();
  }

  // ========== DATA LOADING ==========

  private _loadData(filterDto: Partial<FilterDocumentDto>): void {
    this._documentsApiService
      .filter$(filterDto)
      .pipe(tap((documents) => this.updateData(documents || [])))
      .subscribe();
  }

  // ========== CRUD OPERATIONS (public API) ==========

  public create$(body: CreateDocumentDto): Observable<ResponseDocumentDto> {
    return this._documentsApiService
      .create$(body)
      .pipe(tap(() => this.refresh()));
  }

  public bulkCreate$(
    documents: CreateDocumentDto[],
  ): Observable<BulkOperationResultDto> {
    return this._documentsApiService
      .bulkCreate$(documents)
      .pipe(tap(() => this.refresh()));
  }

  public findOne$(
    documentId: string,
    requestedBy?: string,
  ): Observable<ResponseDocumentDto> {
    // Check cache first
    const cachedDoc = this.data().find((doc) => doc.documentId === documentId);
    if (cachedDoc) {
      return of(cachedDoc);
    }

    // Fetch from API
    return this._documentsApiService.findOne$(documentId, requestedBy);
  }

  public update$(
    documentId: string,
    updateData: Partial<UpdateDocumentDto>,
  ): Observable<ResponseDocumentDto> {
    return this._documentsApiService
      .update$(documentId, updateData)
      .pipe(tap(() => this.refresh()));
  }

  public delete$(
    documentId: string,
    deletedBy?: string,
    deleteS3File?: boolean,
    deletionReason?: string,
  ): Observable<DocumentOperationResponseDto> {
    return this._documentsApiService
      .delete$(documentId, deletedBy, deleteS3File, deletionReason)
      .pipe(tap(() => this.refresh()));
  }

  public getStats$(
    statsDto: Partial<DocumentStatsDto>,
  ): Observable<DocumentStatsResponseDto> {
    return this._documentsApiService.getStats$(statsDto);
  }

  public logDownload$(
    documentId: string,
    requestedBy?: string,
    accessReason?: string,
  ): Observable<DocumentOperationResponseDto> {
    return this._documentsApiService.logDownload$(
      documentId,
      requestedBy,
      accessReason,
    );
  }
}
